// Backend port must match backend_config.json for the current environment
// CRA bakes REACT_APP_* at build time — .env on the server is only used if you build there.
// Default 3030 = dev/remote; for local backend use REACT_APP_BACKEND_PORT=3344 when building.
const DEFAULT_BACKEND_PORT = 3030;

const OVH_DEV_API = "https://api-dev.smartttclub.com/";

function isOvHDevHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  return host === "dev.smartttclub.com" || host.endsWith(".dev.smartttclub.com");
}

function withTrailingSlash(url) {
  const s = String(url || "").trim();
  if (!s) return s;
  return s.endsWith("/") ? s : `${s}/`;
}

const getBackendUrl = () => {
  if (typeof window !== "undefined" && isOvHDevHost(window.location.hostname)) {
    return `${window.location.origin}/`;
  }
  if (typeof window !== "undefined" && window.location.hostname === "193.70.75.208") {
    return `${window.location.protocol}//193.70.75.208:3030/`;
  }
  // GCP dev serves the UI and the API on the same sslip host (nginx).
  if (
    typeof window !== "undefined" &&
    /\.sslip\.io$/i.test(window.location.hostname || "")
  ) {
    return `${window.location.origin}/`;
  }
  // Local CRA: use the page origin so /api and /socket.io go through setupProxy
  // (avoids ERR_CONNECTION_REFUSED when the browser hits :3344 while the FE is on :3000).
  if (
    typeof window !== "undefined" &&
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "development"
  ) {
    const host = String(window.location.hostname || "").toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") {
      return withTrailingSlash(window.location.origin);
    }
  }
  // Runtime override from index.html (no rebuild needed — change window.__BACKEND_URL__ on server)
  if (typeof window !== "undefined" && window.__BACKEND_URL__) {
    let url = String(window.__BACKEND_URL__);
    // Mixed-content: never silently send HTTPS pages to production GCP.
    if (typeof window.location?.protocol === "string" && window.location.protocol === "https:" && url.startsWith("http://")) {
      if (/193\.70\.75\.208/.test(url) || /api-dev\.smartttclub\.com/.test(url)) {
        url = OVH_DEV_API;
      } else {
        url = url.replace(/^http:\/\//, "https://");
      }
    }
    return withTrailingSlash(url);
  }
  // Full URL from env. Must be a direct process.env.REACT_APP_* read so CRA
  // inlines it. Do not guard with `typeof process`: that global is absent in
  // the browser, so the baked URL is skipped and a GCS site calls
  // http://storage.googleapis.com:3030.
  const bakedBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (bakedBackendUrl) {
    return bakedBackendUrl.endsWith("/") ? bakedBackendUrl : `${bakedBackendUrl}/`;
  }
  if (typeof window !== "undefined" && window.REACT_APP_BACKEND_URL) {
    const url = window.REACT_APP_BACKEND_URL;
    return url.endsWith("/") ? url : url + "/";
  }
  // Port override only (e.g. REACT_APP_BACKEND_PORT=3030 when server uses development config)
  const port =
    (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_PORT) ||
    (typeof window !== "undefined" && window.REACT_APP_BACKEND_PORT) ||
    DEFAULT_BACKEND_PORT;
  const host = typeof window !== "undefined" ? window.location.hostname || "localhost" : "localhost";
  return `http://${host}:${port}/`;
};

export const ip_back = getBackendUrl();

/** Base URL for static assets (sounds, images) — runtime override via env-config.js on GCS. */
export function publicAssetUrl(path) {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const runtime =
    typeof window !== "undefined" ? window.__PUBLIC_URL__ : "";
  const baked =
    typeof process !== "undefined" && process.env && process.env.PUBLIC_URL
      ? process.env.PUBLIC_URL
      : "";
  let base = String(runtime || baked || "").replace(/\/$/, "");
  if (!base && typeof document !== "undefined") {
    const env = document.querySelector('script[src*="env-config.js"]');
    const envSrc = env && env.getAttribute("src");
    if (envSrc) {
      base = envSrc.replace(/\/env-config\.js(\?.*)?$/, "");
    }
    if (!base) {
      const bundle = document.querySelector('script[src*="bundle.js"]');
      const bundleSrc = bundle && bundle.getAttribute("src");
      if (bundleSrc) {
        base = bundleSrc.replace(/\/static\/js\/bundle\.js(\?.*)?$/, "");
      }
    }
  }
  return `${base}${normalized}`;
}

export function getRouterBasename() {
  // env-config.js may set "" on apex/OVH — that must win over CRA homepage PUBLIC_URL.
  if (typeof window !== "undefined" && Object.prototype.hasOwnProperty.call(window, "__ROUTER_BASENAME__")) {
    return String(window.__ROUTER_BASENAME__ ?? "");
  }
  const publicUrl =
    (typeof window !== "undefined" && window.__PUBLIC_URL__) ||
    (typeof process !== "undefined" && process.env.PUBLIC_URL) ||
    "";
  if (publicUrl) {
    try {
      const pathname = new URL(publicUrl).pathname.replace(/\/$/, "");
      return pathname || "";
    } catch {
      /* ignore */
    }
  }
  return "";
}

/** Apex/OVH host for public club URLs: https://{brandSubdomainBase}/join/{club_subdomain} */
export const brandSubdomainBase = (() => {
  if (typeof window !== "undefined" && window.REACT_APP_BRAND_SUBDOMAIN_BASE) {
    return String(window.REACT_APP_BRAND_SUBDOMAIN_BASE).toLowerCase();
  }
  if (typeof process !== "undefined" && process.env?.REACT_APP_BRAND_SUBDOMAIN_BASE) {
    return String(process.env.REACT_APP_BRAND_SUBDOMAIN_BASE).toLowerCase();
  }
  return "smartttclub.com";
})();
